<template>
  <div class="font-sans bg-[#f6f4f1] text-[#1f2937] min-h-screen flex flex-col">
    <AppHeader />

    <div class="max-w-7xl mx-auto w-full px-4 py-8">
      <!-- Cabeçalho -->
      <div class="flex items-center gap-3 mb-6">
        <button
          @click="goBack"
          class="flex items-center gap-1 text-sm text-[#152a34] hover:text-[#b99a52] transition font-medium"
        >
          <span class="mdi mdi-arrow-left text-lg"></span>
          Voltar
        </button>
      </div>

      <div class="mb-8">
        <h1 class="text-2xl font-bold text-[#152a34] mb-1">{{ categoryName }}</h1>
        <p v-if="address" class="text-gray-500 text-sm flex items-center gap-1">
          <span class="mdi mdi-map-marker text-[#b99a52]"></span>
          {{ address }}
        </p>
      </div>

      <!-- Skeleton loading -->
      <div v-if="searching" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        <div
          v-for="i in 6"
          :key="i"
          class="bg-white rounded-2xl shadow p-5 border border-[#e0dcd6] animate-pulse"
        >
          <div class="flex items-center gap-3 mb-4">
            <div class="w-14 h-14 bg-gray-200 rounded-full"></div>
            <div class="flex-1">
              <div class="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
              <div class="h-3 bg-gray-100 rounded w-1/2"></div>
            </div>
          </div>
          <div class="h-3 bg-gray-100 rounded w-full mb-2"></div>
          <div class="h-3 bg-gray-100 rounded w-2/3"></div>
        </div>
      </div>

      <!-- Resultados -->
      <div v-if="!searching">
        <div v-if="providers.length === 0" class="text-center py-16 text-gray-400">
          <span class="mdi mdi-account-search text-5xl block mb-3"></span>
          <p class="text-lg">Nenhum profissional encontrado nesta área.</p>
          <p class="text-sm mt-1">Tente outra localização ou escolha outra categoria.</p>
        </div>

        <div v-else>
          <p class="text-sm text-gray-500 mb-4">{{ pagination.total }} profissional(is) encontrado(s)</p>
          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            <ProviderCard
              v-for="provider in providers"
              :key="provider.id"
              :provider="provider"
            />
          </div>

          <!-- Paginação -->
          <div v-if="pagination.lastPage > 1" class="flex justify-center mt-8 gap-2">
            <button
              v-for="p in pagination.lastPage"
              :key="p"
              @click="loadPage(p)"
              :class="[
                'px-4 py-2 rounded-lg text-sm font-medium transition',
                currentPage === p
                  ? 'bg-[#152a34] text-white'
                  : 'bg-white border border-gray-200 hover:border-[#b99a52]'
              ]"
            >{{ p }}</button>
          </div>
        </div>
      </div>
    </div>

    <AppFooter />
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { searchProviders } from '@/services/api'
import AppHeader from '@/components/AppHeader.vue'
import AppFooter from '@/components/AppFooter.vue'
import ProviderCard from '@/components/ProviderCard.vue'

const route = useRoute()
const router = useRouter()

const categoryId = computed(() => route.params.categoryId)
const lat = computed(() => parseFloat(route.query.lat))
const lng = computed(() => parseFloat(route.query.lng))
const radius = computed(() => {
  const r = parseFloat(route.query.radius)
  return Number.isFinite(r) && r > 0 ? r : 20
})
const address = computed(() => route.query.address || route.query.location || '')
const categoryName = computed(() => route.query.categoryName || 'Prestadores')

const providers = ref([])
const pagination = ref({ total: 0, page: 1, limit: 20, lastPage: 1 })
const currentPage = ref(1)
const searching = ref(false)

onMounted(() => {
  loadPage(1)
})

async function loadPage (page) {
  if (!lat.value || !lng.value) return
  searching.value = true
  currentPage.value = page

  try {
    const { data } = await searchProviders({
      lat: lat.value,
      lng: lng.value,
      radius: radius.value,
      category_id: categoryId.value,
      page,
      limit: 20
    })
    providers.value = data.data || []
    pagination.value = data.pagination || { total: 0, page: 1, limit: 20, lastPage: 1 }
  } catch (err) {
    providers.value = []
    console.error('Erro ao buscar prestadores:', err)
  } finally {
    searching.value = false
  }
}

function goBack () {
  router.push({ name: 'provider-search' })
}
</script>
